import { useEffect, useMemo, useRef, RefObject } from 'react';

import { useTranslation } from 'react-i18next';

import { Box, InputAdornment, Stack } from '@mui/material';
import { MidFormData } from 'common/types/mid-form-data';
import { useFormik } from 'formik';
import { AlertCircle } from 'lucide-react';
import { BackToServiceProviderLink } from '../';
import { Button, InlineNotification, TextField, TextFieldClearButton } from '../../../../components';
import { PhonePrefixUtil } from '../../../../utils/phone-prefix-utils';
import yup from '../../../../utils/validator';
import { AuthErrorResponseCode } from '../../../../common/types/auth-error-response';

interface MobileIdSubFormProps {
  authCountryCode: string;
  onSubmitSuccess: (value: MidFormData) => void;
  socketErrorCode: string;
  setSocketErrorCode: (value: string) => void;
  continueButtonRef?: RefObject<HTMLButtonElement | null>;
}

export const MobileIdSubForm = ({
  authCountryCode,
  onSubmitSuccess,
  socketErrorCode,
  setSocketErrorCode,
  continueButtonRef,
}: MobileIdSubFormProps) => {
  const { t } = useTranslation();
  const phoneNumberInputRef = useRef<HTMLInputElement>(null);
  const personCodeInputRef = useRef<HTMLInputElement>(null);

  const phonePrefix = useMemo(() => {
    return PhonePrefixUtil.getPrefixByCountryCode(authCountryCode);
  }, [authCountryCode]);

  const formik = useFormik({
    initialValues: {
      personCode: '',
      phoneNumber: '',
    },
    validationSchema: yup.object().shape({
      personCode: authCountryCode === 'EE' ? yup.string().personalCodeEE() : yup.string().required(),
      phoneNumber: yup.string().phoneNumber().required(),
    }),
    validateOnChange: false,
    onSubmit: ({ personCode, phoneNumber }) => {
      onSubmitSuccess({
        personCode,
        phoneNumber,
      });
    },
  });

  const socketErrorMessage = useMemo(() => {
    return socketErrorCode && socketErrorCode != 'INTERNAL_ERROR'
      ? t(`form.socketError.${socketErrorCode.toUpperCase()}.text`)
      : '';
  }, [socketErrorCode, t]);

  useEffect(() => {
    const screenreaderDiv = document.querySelector('output.screenreader-text');

    if (screenreaderDiv) {
      screenreaderDiv.innerHTML = socketErrorMessage;
    }
  }, [socketErrorMessage]);

  useEffect(() => {
    const formikErrorKeys = Object.keys(formik.errors);
    if (formikErrorKeys.length > 0) {
      const elementId = formikErrorKeys[0];
      const element: HTMLInputElement | null = document.querySelector(`#${elementId}`);
      element?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.isSubmitting]);

  useEffect(() => {
    if (socketErrorCode) return;
    setSocketErrorCode('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <form onSubmit={formik.handleSubmit} autoComplete="off">
        <Stack direction="column" sx={{ gap: '1rem', mb: '2rem' }}>
          <TextField
            id="personCode"
            name="personCode"
            error={formik.touched.personCode && !!formik.errors.personCode}
            helperText={formik.touched.personCode && formik.errors.personCode ? t(formik.errors.personCode) : undefined}
            label={`${t('form.general.personCode')}`}
            value={formik.values.personCode}
            onChange={(e) => {
              const value = e.target.value ?? '';
              formik.setFieldValue('personCode', value.trim());
            }}
            inputRef={personCodeInputRef}
            slotProps={{
              htmlInput: {
                'data-testid': 'personCodeInput',
                inputMode: 'numeric',
              },
              input: {
                endAdornment: (
                <InputAdornment position="end">
                  {formik.touched.personCode && formik.errors.personCode && (
                    <AlertCircle role="alert" color="#DE2700" size="1.5rem"></AlertCircle>
                  )}
                  {formik.values.personCode && (
                    <TextFieldClearButton
                      fieldName={t('form.general.personCode')}
                      onClick={() => {
                        formik.setFieldValue('personCode', '');
                        formik.setFieldTouched('personCode', false);
                        personCodeInputRef.current?.focus();
                      }}
                    ></TextFieldClearButton>
                  )}
                </InputAdornment>
                ),
              },
              formHelperText: {
                role: 'alert',
              },
            }}
          ></TextField>

          <TextField
            id="phoneNumber"
            name="phoneNumber"
            autoComplete="tel-local"
            error={formik.touched.phoneNumber && !!formik.errors.phoneNumber}
            helperText={
              formik.touched.phoneNumber && formik.errors.phoneNumber ? t(formik.errors.phoneNumber) : undefined
            }
            label={`${t('form.general.phoneNumber')}`}
            value={formik.values.phoneNumber}
            onChange={(e) => {
              const value = e.target.value ?? '';
              formik.setFieldValue('phoneNumber', value.trim());
            }}
            inputRef={phoneNumberInputRef}
            type="tel"
            slotProps={{
              htmlInput: {
                'data-testid': 'phoneNumberInput',
                'aria-label': t('form.screenreader.phoneNumberWithoutCountryCode'),
              },
              input: {
                startAdornment: <InputAdornment position="start">{phonePrefix}</InputAdornment>,
                endAdornment: (
                <InputAdornment position="end">
                  {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                    <AlertCircle role="alert" color="#DE2700" size="1.5rem"></AlertCircle>
                  )}
                  {formik.values.phoneNumber && (
                    <TextFieldClearButton
                      fieldName={t('form.general.phoneNumber')}
                      onClick={() => {
                        formik.setFieldValue('phoneNumber', '');
                        formik.setFieldTouched('phoneNumber', false);
                        phoneNumberInputRef.current?.focus();
                      }}
                    ></TextFieldClearButton>
                  )}
                </InputAdornment>
                ),
              },
              formHelperText: {
                role: 'alert',
              },
            }}
          ></TextField>

          {socketErrorCode && socketErrorCode != 'INTERNAL_ERROR' && (
            <InlineNotification appearance="error">
              <div {...{ 'data-testid': 'formError' }}>{socketErrorMessage}</div>
            </InlineNotification>
          )}
        </Stack>

        <Button appearance="primary" size="m" type="submit" buttonRef={continueButtonRef} {...{ 'data-testid': 'continueBtn' }}>
          {t('form.general.continueBtn')}
        </Button>
      </form>

      <Box sx={{ my: '1rem' }}>
        <BackToServiceProviderLink errorCode={AuthErrorResponseCode.USER_CANCEL} />
      </Box>
    </>
  );
};
